/**
 * Changing a field: its name, its type, and what that type carries.
 *
 * The schema is re-read rather than patched. A change to one field can change
 * others -- a formula that reads a renamed column is rewritten, and a formula
 * whose expression changed can retype every field that reads *it* -- and the
 * response says nothing about any of them. Writing the one field back would be
 * right about that field and quietly wrong about the rest. The acting tab is
 * excluded from its own `field_updated` broadcast, so nothing is coming over
 * the socket to do it instead.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Field, FieldUpdate } from '@/api/types'

export function useUpdateField(tableId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      fieldId,
      changes,
    }: {
      fieldId: number
      changes: FieldUpdate
    }): Promise<Field> => {
      const updated = await unwrap<unknown>(
        getApi().PATCH('/api/database/fields/{field_id}/', {
          params: { path: { field_id: fieldId } },
          body: changes,
        }),
      )
      // Cast on the way out rather than in: the response is the field *with its
      // related fields*, a shape the schema gives a separate name to, and
      // nothing here reads them -- the whole list is re-read below instead.
      return updated as Field
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.fields(tableId) })
    },
  })
}

/**
 * Adding a field to a table.
 *
 * The same body a change sends, because it is the same question asked with the
 * boxes empty: a field's type decides which properties exist, so creating one
 * and retyping one are one form and one request.
 *
 * The schema is re-read for the reason a change re-reads it -- a new link row
 * field creates the field on the other side of it, and a new formula can retype
 * whatever reads it -- and the answer is handed to the caller as well, because
 * putting a column *somewhere in particular* needs to know which field it got.
 */
export function useCreateField(tableId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: FieldUpdate): Promise<Field> => {
      const created = await unwrap<unknown>(
        getApi().POST('/api/database/fields/table/{table_id}/', {
          params: { path: { table_id: tableId } },
          // Cast at the boundary, like every field payload: the body is a union
          // over the twenty-seven types and the bag of properties is keyed by
          // strings, so the discriminator cannot be checked against it here.
          body: body as never,
        }),
      )
      return created as Field
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.fields(tableId) })
    },
  })
}

/**
 * Making the table identify its rows by a different column.
 *
 * The primary field is the one the grid pins to the front, the one a link row
 * cell shows, and the one that cannot be hidden -- so changing it changes how
 * every table pointing at this one reads. One request, and the whole schema is
 * re-read afterwards: the old primary field becomes an ordinary one and every
 * link row field elsewhere now shows a different column, none of which the
 * answer mentions.
 */
export function useChangePrimaryField(tableId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fieldId: number): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/database/fields/table/{table_id}/change-primary-field/', {
          params: { path: { table_id: tableId } },
          body: { new_primary_field_id: fieldId },
        }),
      ),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.fields(tableId) })
    },
  })
}
